import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { AlertBanner } from '@/components/campus';
import { Loader2, Building2, User, ArrowRight, Upload } from 'lucide-react';

const ORG_TYPES = [
  { value: 'fraternity', label: 'Fraternity' },
  { value: 'sorority', label: 'Sorority' },
  { value: 'club', label: 'Club' },
  { value: 'promoter', label: 'Event Promoter' },
  { value: 'student_union', label: 'Student Union' },
];

export const Onboarding = () => {
  const [step, setStep] = useState('choice'); // 'choice', 'org', 'student'
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  // Org form state
  const [orgName, setOrgName] = useState('');
  const [orgType, setOrgType] = useState('');
  const [campus, setCampus] = useState('');
  const [membershipSize, setMembershipSize] = useState('');

  // Student form state
  const [displayName, setDisplayName] = useState('');
  const [studentCampus, setStudentCampus] = useState('');
  const [classYear, setClassYear] = useState('');

  const { user, createOrganization, createProfile, signOut } = useAuth();
  const navigate = useNavigate();

  const handleOrgSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      await createOrganization(
        {
          name: orgName,
          type: orgType,
          membershipSize: membershipSize ? parseInt(membershipSize) : null,
        },
        campus
      );

      navigate('/app/command-center', { replace: true });
    } catch (err) {
      setError(err.message || 'Failed to create organization');
    } finally {
      setIsLoading(false);
    }
  };

  const handleStudentSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      await createProfile({
        display_name: displayName,
        class_year: classYear ? parseInt(classYear) : null,
      });

      navigate('/discover', { replace: true });
    } catch (err) {
      setError(err.message || 'Failed to save profile');
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = async () => {
    await signOut();
    navigate('/login', { replace: true });
  };

  // Choice screen
  if (step === 'choice') {
    return (
      <div className="min-h-screen bg-obsidian flex items-center justify-center p-4">
        <div className="w-full max-w-lg">
          {/* Logo */}
          <div className="flex flex-col items-center mb-10">
            <Link to="/" className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-xl bg-electric-ice-blue flex items-center justify-center">
                <span className="text-white font-bold text-xl">C</span>
              </div>
              <span className="text-2xl font-semibold tracking-[-0.02em] text-cloud-white">
                CamPassed
              </span>
            </Link>
            <p className="text-soft-silver/70 text-sm tracking-wide">
              The operating system for campus social life
            </p>
          </div>

          <div className="text-center mb-8">
            <h1 className="text-2xl font-semibold text-cloud-white tracking-[-0.02em]">
              Welcome to CamPassed
            </h1>
            <p className="text-soft-silver text-sm mt-2">
              How would you like to use CamPassed?
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* Organization option - Ice Blue accent */}
            <button
              onClick={() => setStep('org')}
              className="bg-graphite rounded-xl border border-electric-ice-blue/20 p-6 text-left hover:border-electric-ice-blue/60 hover:bg-electric-ice-blue/[0.03] transition-all duration-200 group relative overflow-hidden"
              data-testid="onboard-org-choice"
            >
              {/* Subtle gradient glow */}
              <div className="absolute inset-0 bg-gradient-to-br from-electric-ice-blue/[0.08] to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
              
              <div className="relative">
                <div className="w-12 h-12 rounded-lg bg-electric-ice-blue/10 border border-electric-ice-blue/20 flex items-center justify-center mb-4 group-hover:bg-electric-ice-blue/20 group-hover:border-electric-ice-blue/40 transition-all">
                  <Building2 className="w-6 h-6 text-electric-ice-blue" />
                </div>
                <h3 className="text-lg font-medium text-cloud-white mb-2">
                  Create an Organization
                </h3>
                <p className="text-sm text-soft-silver">
                  Set up your fraternity, sorority, club, or event promoter account
                </p>
                <div className="mt-4 flex items-center text-electric-ice-blue text-sm font-medium opacity-0 group-hover:opacity-100 transition-opacity">
                  Get started <ArrowRight className="w-4 h-4 ml-1" />
                </div>
              </div>
            </button>

            {/* Student option - Cool Mint accent */}
            <button
              onClick={() => setStep('student')}
              className="bg-graphite rounded-xl border border-cool-mint/20 p-6 text-left hover:border-cool-mint/60 hover:bg-cool-mint/[0.03] transition-all duration-200 group relative overflow-hidden"
              data-testid="onboard-student-choice"
            >
              {/* Subtle gradient glow */}
              <div className="absolute inset-0 bg-gradient-to-br from-cool-mint/[0.08] to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
              
              <div className="relative">
                <div className="w-12 h-12 rounded-lg bg-cool-mint/10 border border-cool-mint/20 flex items-center justify-center mb-4 group-hover:bg-cool-mint/20 group-hover:border-cool-mint/40 transition-all">
                  <User className="w-6 h-6 text-cool-mint" />
                </div>
                <h3 className="text-lg font-medium text-cloud-white mb-2">
                  Join as Student
                </h3>
                <p className="text-sm text-soft-silver">
                  Discover events, get passes, and track your campus journey
                </p>
                <div className="mt-4 flex items-center text-cool-mint text-sm font-medium opacity-0 group-hover:opacity-100 transition-opacity">
                  Get started <ArrowRight className="w-4 h-4 ml-1" />
                </div>
              </div>
            </button>
          </div>

          <p className="text-center text-sm text-muted-text mt-8">
            Signed in as {user?.email} •{' '}
            <button onClick={handleLogout} className="text-electric-ice-blue hover:underline">
              Sign out
            </button>
          </p>
        </div>
      </div>
    );
  }

  // Organization onboarding
  if (step === 'org') {
    return (
      <div className="min-h-screen bg-obsidian flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          {/* Logo */}
          <div className="flex justify-center mb-8">
            <Link to="/" className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-electric-ice-blue flex items-center justify-center">
                <span className="text-white font-bold text-xl">C</span>
              </div>
              <span className="text-2xl font-semibold tracking-[-0.02em] text-cloud-white">
                CamPassed
              </span>
            </Link>
          </div>

          <div className="bg-graphite rounded-2xl border border-white/[0.08] p-8">
            <div className="text-center mb-6">
              <h1 className="text-2xl font-semibold text-cloud-white tracking-[-0.02em]">
                Create your organization
              </h1>
              <p className="text-soft-silver text-sm mt-2">
                Set up your organization profile
              </p>
            </div>

            {error && (
              <div className="mb-4">
                <AlertBanner variant="error" message={error} dismissible onDismiss={() => setError('')} />
              </div>
            )}

            <form onSubmit={handleOrgSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="orgName" className="text-soft-silver">Organization Name</Label>
                <Input
                  id="orgName"
                  type="text"
                  value={orgName}
                  onChange={(e) => setOrgName(e.target.value)}
                  placeholder="Alpha Sigma Phi"
                  className="bg-obsidian border-white/[0.08] text-cloud-white placeholder:text-muted-text focus:border-electric-ice-blue"
                  data-testid="org-name"
                  required
                  disabled={isLoading}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="orgType" className="text-soft-silver">Organization Type</Label>
                <Select value={orgType} onValueChange={setOrgType} required disabled={isLoading}>
                  <SelectTrigger 
                    className="bg-obsidian border-white/[0.08] text-cloud-white focus:border-electric-ice-blue"
                    data-testid="org-type"
                  >
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent className="bg-graphite border-white/[0.08]">
                    {ORG_TYPES.map((type) => (
                      <SelectItem 
                        key={type.value} 
                        value={type.value}
                        className="text-cloud-white hover:bg-white/[0.05] focus:bg-white/[0.05]"
                      >
                        {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="campus" className="text-soft-silver">Campus</Label>
                <Input
                  id="campus"
                  type="text"
                  value={campus}
                  onChange={(e) => setCampus(e.target.value)}
                  placeholder="State University"
                  className="bg-obsidian border-white/[0.08] text-cloud-white placeholder:text-muted-text focus:border-electric-ice-blue"
                  data-testid="org-campus"
                  required
                  disabled={isLoading}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="membershipSize" className="text-soft-silver">
                  Approximate Membership Size
                  <span className="text-muted-text ml-1">(optional)</span>
                </Label>
                <Input
                  id="membershipSize"
                  type="number"
                  value={membershipSize}
                  onChange={(e) => setMembershipSize(e.target.value)}
                  placeholder="50"
                  className="bg-obsidian border-white/[0.08] text-cloud-white placeholder:text-muted-text focus:border-electric-ice-blue"
                  data-testid="org-size"
                  disabled={isLoading}
                  min="1"
                />
              </div>

              {/* Logo placeholder */}
              <div className="space-y-2">
                <Label className="text-soft-silver">Organization Logo <span className="text-muted-text">(optional)</span></Label>
                <div className="border-2 border-dashed border-white/[0.08] rounded-xl p-6 text-center hover:border-white/[0.15] transition-colors cursor-pointer">
                  <Upload className="w-8 h-8 text-muted-text mx-auto mb-2" />
                  <p className="text-sm text-muted-text">Coming soon</p>
                </div>
              </div>

              <div className="flex gap-3 pt-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setStep('choice')}
                  className="flex-1 border-white/[0.08] text-soft-silver hover:bg-white/[0.05]"
                  disabled={isLoading}
                >
                  Back
                </Button>
                <Button 
                  type="submit" 
                  className="flex-1 bg-electric-ice-blue text-white hover:bg-electric-ice-blue/90 font-medium"
                  data-testid="org-submit"
                  disabled={isLoading || !orgName || !orgType || !campus}
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Creating...
                    </>
                  ) : (
                    <>
                      Continue
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </>
                  )}
                </Button>
              </div>
            </form>
          </div>
        </div>
      </div>
    );
  }

  // Student onboarding
  if (step === 'student') {
    return (
      <div className="min-h-screen bg-obsidian flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          {/* Logo */}
          <div className="flex justify-center mb-8">
            <Link to="/" className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-electric-ice-blue flex items-center justify-center">
                <span className="text-white font-bold text-xl">C</span>
              </div>
              <span className="text-2xl font-semibold tracking-[-0.02em] text-cloud-white">
                CamPassed
              </span>
            </Link>
          </div>

          <div className="bg-graphite rounded-2xl border border-white/[0.08] p-8">
            <div className="text-center mb-6">
              <h1 className="text-2xl font-semibold text-cloud-white tracking-[-0.02em]">
                Complete your profile
              </h1>
              <p className="text-soft-silver text-sm mt-2">
                Tell us a bit about yourself
              </p>
            </div>

            {error && (
              <div className="mb-4">
                <AlertBanner variant="error" message={error} dismissible onDismiss={() => setError('')} />
              </div>
            )}

            <form onSubmit={handleStudentSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="displayName" className="text-soft-silver">Display Name</Label>
                <Input
                  id="displayName"
                  type="text"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  placeholder="John Doe"
                  className="bg-obsidian border-white/[0.08] text-cloud-white placeholder:text-muted-text focus:border-electric-ice-blue"
                  data-testid="student-name"
                  required
                  disabled={isLoading}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="studentCampus" className="text-soft-silver">Campus</Label>
                <Input
                  id="studentCampus"
                  type="text"
                  value={studentCampus}
                  onChange={(e) => setStudentCampus(e.target.value)}
                  placeholder="State University"
                  className="bg-obsidian border-white/[0.08] text-cloud-white placeholder:text-muted-text focus:border-electric-ice-blue"
                  data-testid="student-campus"
                  required
                  disabled={isLoading}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="classYear" className="text-soft-silver">
                  Class Year
                  <span className="text-muted-text ml-1">(optional)</span>
                </Label>
                <Input
                  id="classYear"
                  type="number"
                  value={classYear}
                  onChange={(e) => setClassYear(e.target.value)}
                  placeholder="2027"
                  className="bg-obsidian border-white/[0.08] text-cloud-white placeholder:text-muted-text focus:border-electric-ice-blue"
                  data-testid="student-year"
                  disabled={isLoading}
                  min="2020"
                  max="2035"
                />
              </div>

              <div className="flex gap-3 pt-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setStep('choice')}
                  className="flex-1 border-white/[0.08] text-soft-silver hover:bg-white/[0.05]"
                  disabled={isLoading}
                >
                  Back
                </Button>
                <Button 
                  type="submit" 
                  className="flex-1 bg-electric-ice-blue text-white hover:bg-electric-ice-blue/90 font-medium"
                  data-testid="student-submit"
                  disabled={isLoading || !displayName || !studentCampus}
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      Get Started
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </>
                  )}
                </Button>
              </div>
            </form>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default Onboarding;
